/**
 * The Billing tab's Section 1 — the tenant's subscription history.
 *
 * A per-term table (start, end, plan, payment amount, total paid, status, next
 * payment) whose rows expand to the individual payments recorded against each
 * term, plus a folded "Add subscription" form. Reads
 * `listSubscriptionTermsWithBilling` (Task 5.1/5.4): each term is joined with
 * its payment log, and the CURRENT term's status/next-payment are derived from
 * that ledger + the cycle — the tenant's `manual_billing_paid_through` column is
 * no longer read or edited here.
 *
 * The Billing tab was reduced to Subscription + Promotions on 2026-08-26, which
 * removed the old "Payment state" block that used to sit above this table
 * (paid-through editor, non-payment fallback, contract-price editor, restore
 * ceilings). The non-payment fallback still lives on the Operations manual-billing
 * queue (`ManualBillingQueue`); nothing else moved.
 *
 * The add-term form's UI PATTERNS (plan/date/cycle fields, the sized
 * >1-month-cycle confirmation, the 409-conflict inline message, future-only
 * delete) are unchanged from the previous version; only the surrounding chrome
 * (an `ActionDisclosure` instead of an always-open inline form) changed. It is
 * NOT a `Modal`: `submitAdd` calls `confirm()` for the cycle-impact case, and
 * the shared `Modal` marks the rest of the document `inert`, which would trap
 * that confirmation dialog in a non-interactive subtree.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast, useConfirm, useRefreshWhenShown, useDraftState, useDraftInFlight, ActionDisclosure } from '@papan/core-ui/ui'
import {
  formatCurrencyMinor, parseMajorToMinor, currencyMinorUnitsPerMajor,
  CYCLE_UNITS, type CycleUnit, formatPlatformDate,
} from '@papan/types'
import {
  budgetApi, type ManualBillingResponse, type TermBillingRow,
} from '../../client/budget'
import { planAdminApi, type PlanPickerOption } from '@papan/billing/client'
import { Section } from './bits'

/** Major-unit text -> minor units, accepting ZERO (a waived term/payment is
 *  real; `parseMajorToMinor` refuses 0 because it exists for catalog prices). */
/** No row open — shared so the fallback is one stable value. */
const NO_ROWS: Set<string> = new Set()

function parseAmount(text: string, currency: string): number | null {
  const cleaned = text.trim().replace(/,/g, '')
  if (cleaned === '') return null
  if (/^0+(\.0+)?$/.test(cleaned)) return 0
  return parseMajorToMinor(cleaned, currency)
}

/**
 * `true` when a cycle bills less often than monthly — a year at any count, or
 * more than one month. Day/week cycles never trip it: they are the ordinary
 * rhythms this screen already handles without a special confirmation, and the
 * risk this guards against ("a rhythm change with the amount on screen
 * unchanged") is specifically the multi-month/annual case.
 */
function cycleExceedsOneMonth(cycle: { count: number; unit: CycleUnit }): boolean {
  if (cycle.unit === 'year') return true
  if (cycle.unit === 'month') return cycle.count > 1
  return false
}

/** "monthly" / "yearly" / "every 2 weeks" — the rhythm in prose, for a
 *  confirmation sentence. */
function rhythmWords(
  cycle: { count: number; unit: CycleUnit },
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  if (cycle.count === 1) {
    return t(`settings.platform.billing.history.rhythm.${cycle.unit}`, {
      defaultValue: { day: 'daily', week: 'weekly', month: 'monthly', year: 'yearly' }[cycle.unit],
    })
  }
  return t('settings.platform.billing.history.rhythm.everyN', {
    defaultValue: 'every {{count}} {{unit}}s', count: cycle.count, unit: cycle.unit,
  })
}

export function SubscriptionHistory({
  tenantId, canEdit, onOpenPlan, onPlanChanged,
}: {
  tenantId: string
  /** `customers:write` — every term-history action (add, delete, record
   *  payment, edit amount). */
  canEdit: boolean
  /** The current term's plan cell opens the Plan tab, where the assignment
   *  actually lives. */
  onOpenPlan: () => void
  /**
   * Fired after a term add/delete succeeds — every action that CAN change the
   * tenant's effective plan. `CustomerDetail` shows the plan (its header's
   * name/label, `HealthBadge`) and the uploads-paused banner from its OWN `row`,
   * which this component never touches — without this callback that header goes
   * stale until the keep-alive deck is next revisited. Optional —
   * `SubscriptionHistory.test.tsx` renders this component standalone.
   */
  onPlanChanged?: () => void | Promise<void>
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const confirm = useConfirm()

  const [billing, setBilling] = useState<ManualBillingResponse | null>(null)
  const [terms, setTerms] = useState<TermBillingRow[] | null>(null)
  const [plans, setPlans] = useState<PlanPickerOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [b, tr, pl] = await Promise.all([
        budgetApi.getManualBilling(tenantId),
        budgetApi.listSubscriptionTermsWithBilling(tenantId),
        planAdminApi.get(tenantId),
      ])
      setBilling(b)
      setTerms(tr.terms)
      setPlans(pl.plans)
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load subscription history')
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => { void load() }, [load])

  const planName = (planId: string) => plans.find(p => p.id === planId)?.name ?? planId
  const activePlans = plans.filter(p => p.isActive)

  // Newest first, like the Configuration-tab timeline.
  const sortedTerms = terms
    ? [...terms].sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime())
    : []

  // ── Add-term form ───────────────────────────────────────────────────────
  // Every unsaved field below is a draft kept across a phone/desktop layout
  // swap (#897), under `subs:<tenant>:…`.
  const dk = `subs:${tenantId}`
  const [addPlanId, setAddPlanId, { clear: clearAddPlanId }] = useDraftState(`${dk}:add:plan`, '')
  const [addDate, setAddDate, { clear: clearAddDate }] = useDraftState(`${dk}:add:date`, '')
  const [addCycleCount, setAddCycleCount, { clear: clearAddCycleCount }] = useDraftState(`${dk}:add:cycleCount`, '1')
  const [addCycleUnit, setAddCycleUnit, { clear: clearAddCycleUnit }] = useDraftState<CycleUnit>(`${dk}:add:cycleUnit`, 'month')
  const [addAmountText, setAddAmountText, { clear: clearAddAmountText }] = useDraftState(`${dk}:add:amount`, '')
  const [addSavingLocal, setAddSaving] = useState(false)
  // #897 — both writes append to a ledger (no delete): marked in flight in the
  // draft store too, so a form a layout swap restored mid-request can't send
  // the same term / payment a second time.
  // One that finished on the screen a swap replaced reloaded THAT screen's
  // list: this one clears what came back filled in and reloads its own.
  // `value` is whether it WROTE: a refused one (a 409, a network error) leaves
  // the restored form as it is, to fix and retry — Add answers with the
  // server's reason again (the first one went to the gone screen).
  const [addInFlight, trackAdd] = useDraftInFlight<boolean>(`${dk}:add`, (o) => {
    if (!o.ok || !o.value) return
    clearAddPlanId(); clearAddDate(); clearAddCycleCount(); clearAddCycleUnit(); clearAddAmountText()
    void load().then(() => onPlanChanged?.())
  })
  const addSaving = addSavingLocal || addInFlight
  const [addError, setAddError] = useState<string | null>(null)
  const addFormDirty = addPlanId !== '' || addDate !== '' || addAmountText.trim() !== ''
  const [addOpen, setAddOpen] = useDraftState<boolean>(tenantId ? `subs:${tenantId}:add:open` : null, false)

  // ── Per-row expand / edit-amount / record-payment ───────────────────────
  // Which rows are open rides along, so a row form a swap carried over is
  // still on screen afterwards.
  const [expanded, setExpanded] = useDraftState<Set<string>>(`${dk}:expanded`, NO_ROWS)
  const [editingAmountId, setEditingAmountId, { clear: clearEditingAmountId }] = useDraftState<string | null>(`${dk}:row:editingAmountId`, null)
  const [editAmountText, setEditAmountText, { clear: clearEditAmountText }] = useDraftState(`${dk}:row:editAmount`, '')
  const [editAmountSaving, setEditAmountSaving] = useState(false)
  const [payAmountText, setPayAmountText, { clear: clearPayAmountText }] = useDraftState(`${dk}:row:payAmount`, '')
  const [payReference, setPayReference, { clear: clearPayReference }] = useDraftState(`${dk}:row:payReference`, '')
  const [paySavingLocal, setPaySaving] = useState(false)
  const [payInFlight, trackPay] = useDraftInFlight<boolean>(`${dk}:row:pay`, (o) => {
    if (!o.ok || !o.value) return
    clearPayAmountText(); clearPayReference()
    void load()
  })
  const paySaving = paySavingLocal || payInFlight
  const [payError, setPayError] = useState<string | null>(null)
  const rowFormsDirty = editingAmountId != null || payAmountText.trim() !== '' || payReference.trim() !== ''

  useRefreshWhenShown(() => {
    if (!addFormDirty && !rowFormsDirty) void load()
  })

  if (!billing || !terms) {
    if (loading) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
    return <div className="p-4 text-sm text-red-400">{error}</div>
  }

  const manual = billing.planAuthority === 'manual'
  const canWrite = canEdit && manual

  const submitAdd = async () => {
    setAddError(null)
    if (!addPlanId) {
      setAddError(t('settings.platform.billing.history.addMissingPlan', { defaultValue: 'Choose a plan.' }))
      return
    }
    if (!addDate) {
      setAddError(t('settings.platform.billing.history.addMissingDate', { defaultValue: 'Choose an effective date.' }))
      return
    }
    const count = Number(addCycleCount)
    if (!Number.isInteger(count) || count < 1) {
      setAddError(t('settings.platform.billing.history.badCount', { defaultValue: 'The cycle count must be a whole number of 1 or more.' }))
      return
    }
    const amountMinor = parseAmount(addAmountText, billing.currency)
    if (addAmountText.trim() !== '' && amountMinor == null) {
      setAddError(t('settings.platform.billing.history.amountInvalid', { defaultValue: 'Enter a non-negative amount.' }))
      return
    }

    // Sized confirmation — a >1-month rhythm bills the SAME amount less often,
    // an invoice consequence invisible in the fields being edited, so it is
    // stated before the write. The operator-entered amount is shown as typed —
    // never auto-converted for the new rhythm.
    const cycle = { count, unit: addCycleUnit }
    if (cycleExceedsOneMonth(cycle)) {
      const plan = activePlans.find(p => p.id === addPlanId)
      const ok = await confirm({
        title: t('settings.platform.billing.history.cycleImpactTitle', {
          defaultValue: 'Add this {{rhythm}} plan change?', rhythm: rhythmWords(cycle, t),
        }),
        message: [
          amountMinor == null
            ? t('settings.platform.billing.history.cycleImpactUnpriced', {
                defaultValue: 'No payment amount was entered, so there is no amount to re-time.',
              })
            : t('settings.platform.billing.history.cycleImpactBody', {
                defaultValue:
                  '{{amount}} will be charged {{rhythm}} for {{plan}}, starting {{date}} — the amount does not change, only how often it is billed.',
                amount: formatCurrencyMinor(amountMinor, billing.currency),
                rhythm: rhythmWords(cycle, t),
                plan: plan?.name ?? addPlanId,
                date: formatPlatformDate(`${addDate}T00:00:00Z`),
              }),
          t('settings.platform.billing.history.cycleImpactAllowance', {
            defaultValue: 'The AI allowance is unaffected — it resets on its own monthly rhythm.',
          }),
        ].join('\n\n'),
        confirmText: t('settings.platform.billing.history.cycleImpactOk', { defaultValue: 'Add plan change' }),
      })
      if (!ok) return
    }

    if (addSaving) return
    setAddSaving(true)
    await trackAdd(async () => { try {
      await budgetApi.addSubscriptionTerm(tenantId, {
        planId: addPlanId,
        effectiveFrom: new Date(`${addDate}T00:00:00Z`).toISOString(),
        cycleCount: count,
        cycleUnit: addCycleUnit,
        paymentAmountMinor: amountMinor,
      })
      toast.success(t('settings.platform.billing.history.added', { defaultValue: 'Subscription added.' }))
      clearAddPlanId(); clearAddDate(); clearAddCycleCount(); clearAddCycleUnit(); clearAddAmountText()
      await load()
      // A term effective NOW or in the past supersedes the current one, so an
      // add can change the tenant's effective plan immediately — refresh the
      // parent header rather than leave it stale until the deck is revisited.
      await onPlanChanged?.()
      return true
    } catch (e: any) {
      if (e?.code === 'SUBSCRIPTION_TERM_CONFLICT' || e?.status === 409) {
        setAddError(t('settings.platform.billing.history.addConflict', { defaultValue: 'A subscription already starts on that date for this tenant.' }))
      } else {
        toast.error(e?.message || 'Could not add the subscription')
      }
      return false
    } finally {
      setAddSaving(false)
    } })
  }

  const handleDelete = async (term: TermBillingRow) => {
    const ok = await confirm({
      title: t('settings.platform.billing.history.deleteConfirmTitle', { defaultValue: 'Delete this scheduled plan change?' }),
      message: t('settings.platform.billing.history.deleteConfirmBody', {
        defaultValue: '{{plan}}, effective {{date}}, will be removed.', plan: planName(term.planId), date: formatPlatformDate(term.effectiveFrom),
      }),
      confirmText: t('settings.platform.billing.history.deleteConfirmOk', { defaultValue: 'Delete' }),
      type: 'danger',
    })
    if (!ok) return

    try {
      await budgetApi.deleteTerm(tenantId, term.id)
      toast.success(t('settings.platform.billing.history.deleted', { defaultValue: 'Plan change deleted.' }))
      await load()
      // Only a FUTURE term is deletable (the button is gated on `isFuture`),
      // so this never changes the CURRENT effective plan — called anyway,
      // cheap, and one fewer place to keep in sync with which terms delete.
      await onPlanChanged?.()
    } catch (e: any) {
      // The DELETE route returns TWO distinct 409s and they mean different
      // things: a FUTURE term that acquired payments (record while current, then
      // PATCH its date back to the future) is `TERM_HAS_PAYMENTS`, while a term
      // in force is `SUBSCRIPTION_TERM_ALREADY_EFFECTIVE`. Collapsing both into
      // the "already taken effect" message — as `|| status === 409` did — told
      // an operator the wrong reason for the one that has payments.
      if (e?.code === 'TERM_HAS_PAYMENTS') {
        toast.error(t('settings.platform.billing.history.deletePaymentsConflict', { defaultValue: 'This term has recorded payments and can no longer be deleted.' }))
      } else if (e?.code === 'SUBSCRIPTION_TERM_ALREADY_EFFECTIVE' || e?.status === 409) {
        toast.error(t('settings.platform.billing.history.deleteConflict', { defaultValue: 'This term has already taken effect and can no longer be deleted.' }))
      } else {
        toast.error(e?.message || 'Could not delete the plan change')
      }
    }
  }

  const startEditAmount = (term: TermBillingRow) => {
    setEditingAmountId(term.id)
    setEditAmountText(
      term.paymentAmountMinor != null
        ? String(term.paymentAmountMinor / currencyMinorUnitsPerMajor(billing.currency))
        : '',
    )
  }

  const submitEditAmount = async (term: TermBillingRow) => {
    const amountMinor = parseAmount(editAmountText, billing.currency)
    if (editAmountText.trim() !== '' && amountMinor == null) return
    setEditAmountSaving(true)
    try {
      await budgetApi.updateTerm(tenantId, term.id, { paymentAmountMinor: amountMinor })
      toast.success(t('settings.platform.billing.history.amountUpdated', { defaultValue: 'Payment amount updated.' }))
      clearEditingAmountId(); clearEditAmountText()
      await load()
    } catch (e: any) {
      toast.error(e?.message || 'Could not save the amount')
    } finally {
      setEditAmountSaving(false)
    }
  }

  const submitPayment = async (term: TermBillingRow) => {
    const amountMinor = parseAmount(payAmountText, billing.currency)
    if (amountMinor == null) {
      setPayError(t('settings.platform.billing.history.amountInvalid', { defaultValue: 'Enter a non-negative amount.' }))
      return
    }
    if (paySaving) return
    setPayError(null)
    setPaySaving(true)
    await trackPay(async () => { try {
      await budgetApi.recordTermPayment(tenantId, term.id, {
        amountMinor,
        paidAt: new Date().toISOString(),
        reference: payReference.trim() || null,
      })
      toast.success(t('settings.platform.billing.history.recorded', { defaultValue: 'Payment recorded.' }))
      clearPayAmountText(); clearPayReference()
      await load()
      return true
    } catch (e: any) {
      if (e?.code === 'DUPLICATE_PAYMENT_REFERENCE' || e?.status === 409) {
        setPayError(t('settings.platform.billing.history.duplicateRef', {
          defaultValue: 'A payment with this reference is already recorded against this term.',
        }))
      } else {
        toast.error(e?.message || 'Could not record the payment')
      }
      return false
    } finally {
      setPaySaving(false)
    } })
  }

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  return (
    <Section
      title={t('settings.platform.billing.history.title', { defaultValue: 'Subscription' })}
      info={t('settings.platform.billing.history.desc', {
        defaultValue: 'Every plan and billing-rhythm change for this tenant, with what was charged and paid against each.',
      })}
    >
      {!manual && (
        <div className="rounded-lg border themed-border px-3 py-2 mb-3">
          <p className="themed-text text-xs font-medium">
            {t('settings.platform.billing.history.notManualTitle', { defaultValue: 'This deployment\'s plans come from the payment provider.' })}
          </p>
          <p className="themed-text-secondary text-xs mt-0.5">
            {t('settings.platform.billing.history.notManualBody', {
              defaultValue: 'Subscription terms are read-only here. Switch Platform settings → "Who decides tenants\' plans" to "An operator, manually" to record payments and manage subscription terms.',
            })}
          </p>
        </div>
      )}

      <div className="themed-bg-secondary rounded-lg overflow-hidden">
        {sortedTerms.length === 0 && (
          <div className="px-3 py-3 themed-text-muted text-xs">
            {t('settings.platform.billing.history.empty', { defaultValue: 'No subscriptions recorded yet.' })}
          </div>
        )}
        {sortedTerms.length > 0 && (
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_1fr_10.5rem] gap-2 px-3 py-1.5 themed-text-muted text-[10px] uppercase tracking-wide border-b themed-border">
            <span>{t('settings.platform.billing.history.colStart', { defaultValue: 'Start' })}</span>
            <span>{t('settings.platform.billing.history.colEnd', { defaultValue: 'End' })}</span>
            <span>{t('settings.platform.billing.history.colPlan', { defaultValue: 'Plan' })}</span>
            <span>{t('settings.platform.billing.history.colAmount', { defaultValue: 'Payment amount' })}</span>
            <span>{t('settings.platform.billing.history.colTotalPaid', { defaultValue: 'Total paid' })}</span>
            <span>{t('settings.platform.billing.history.colStatus', { defaultValue: 'Status' })}</span>
            <span>{t('settings.platform.billing.history.colNext', { defaultValue: 'Next payment' })}</span>
            <span />
          </div>
        )}
        {sortedTerms.map(term => {
          const isFuture = !term.isCurrent && (term.appliedAt === null || new Date(term.effectiveFrom) > new Date())
          const isOpen = expanded.has(term.id)
          return (
            <div key={term.id} className="border-b themed-border last:border-b-0">
              <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_1fr_10.5rem] gap-2 items-center px-3 py-2 text-xs">
                <span className="themed-text-muted">{formatPlatformDate(term.effectiveFrom)}</span>
                <span className="themed-text-muted">
                  {term.endAt === null
                    ? t('settings.platform.billing.history.present', { defaultValue: 'Present' })
                    : formatPlatformDate(term.endAt)}
                </span>
                {term.isCurrent ? (
                  <button
                    onClick={onOpenPlan}
                    className="themed-text text-left underline underline-offset-2 hover:themed-text-secondary truncate"
                  >
                    {planName(term.planId)}
                  </button>
                ) : (
                  <span className="themed-text truncate">{planName(term.planId)}</span>
                )}
                <span className="themed-text tabular-nums">
                  {term.paymentAmountMinor == null ? '—' : formatCurrencyMinor(term.paymentAmountMinor, billing.currency)}
                </span>
                <span className="themed-text tabular-nums">{formatCurrencyMinor(term.totalPaidMinor, billing.currency)}</span>
                <span><StatusChip status={term.status} t={t} /></span>
                <span className="themed-text-muted">
                  {term.nextPaymentAt ? formatPlatformDate(term.nextPaymentAt) : '—'}
                </span>
                <div className="flex items-center gap-1 justify-end">
                  <button
                    onClick={() => toggleExpand(term.id)}
                    aria-label={`${planName(term.planId)} — ${isOpen
                      ? t('settings.platform.billing.history.collapse', { defaultValue: 'Hide payments' })
                      : t('settings.platform.billing.history.expand', { defaultValue: 'Show payments' })}`}
                    className="px-2 py-1 text-[11px] rounded themed-btn-secondary"
                  >
                    {isOpen
                      ? t('settings.platform.billing.history.collapse', { defaultValue: 'Hide payments' })
                      : t('settings.platform.billing.history.expand', { defaultValue: 'Show payments' })}
                  </button>
                  {canWrite && isFuture && (
                    <button
                      onClick={() => handleDelete(term)}
                      aria-label={`${planName(term.planId)} — ${t('settings.platform.billing.history.delete', { defaultValue: 'Delete' })}`}
                      className="px-2 py-1 text-[11px] rounded border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                    >
                      {t('settings.platform.billing.history.delete', { defaultValue: 'Delete' })}
                    </button>
                  )}
                </div>
              </div>

              {isOpen && (
                <div className="px-3 pb-3">
                  {term.payments.length === 0 ? (
                    <p className="themed-text-muted text-[11px]">
                      {t('settings.platform.billing.history.paymentsEmpty', { defaultValue: 'No payments recorded against this term.' })}
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {term.payments.map(p => (
                        <div key={p.id} className="flex items-center gap-3 text-[11px] themed-text-secondary">
                          <span className="tabular-nums themed-text">{formatCurrencyMinor(p.amountMinor, p.billingCurrency)}</span>
                          <span>{formatPlatformDate(p.paidAt)}</span>
                          {p.reference && <span>{p.reference}</span>}
                          {p.note && <span className="themed-text-muted">{p.note}</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {canWrite && term.isCurrent && (
                    <div className="mt-3 pt-2 border-t themed-border flex flex-wrap items-end gap-2">
                      {editingAmountId === term.id ? (
                        <>
                          <label className="flex flex-col gap-1">
                            <span className="themed-text-secondary text-[11px]">
                              {t('settings.platform.billing.history.editAmount', { defaultValue: 'Edit amount' })}
                            </span>
                            <input
                              value={editAmountText}
                              onChange={e => setEditAmountText(e.target.value)}
                              disabled={editAmountSaving}
                              inputMode="decimal"
                              aria-label={t('settings.platform.billing.history.editAmount', { defaultValue: 'Edit amount' })}
                              className="themed-input border rounded px-2 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
                            />
                          </label>
                          <button
                            onClick={() => submitEditAmount(term)}
                            disabled={editAmountSaving}
                            className="px-2.5 py-1 text-[11px] rounded themed-btn-primary disabled:opacity-50"
                          >
                            {editAmountSaving ? t('common:saving') : t('common:save')}
                          </button>
                          <button
                            onClick={() => { clearEditingAmountId(); clearEditAmountText() }}
                            disabled={editAmountSaving}
                            className="px-2.5 py-1 text-[11px] rounded themed-btn-secondary disabled:opacity-50"
                          >
                            {t('common:cancel')}
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => startEditAmount(term)}
                          className="text-[11px] themed-text-muted hover:themed-text underline underline-offset-2"
                        >
                          {t('settings.platform.billing.history.editAmount', { defaultValue: 'Edit amount' })}
                        </button>
                      )}

                      <label className="flex flex-col gap-1">
                        <span className="themed-text-secondary text-[11px]">
                          {t('settings.platform.billing.history.amount', { defaultValue: 'Amount ({{currency}})', currency: billing.currency.toUpperCase() })}
                        </span>
                        <input
                          value={payAmountText}
                          onChange={e => setPayAmountText(e.target.value)}
                          disabled={paySaving}
                          inputMode="decimal"
                          aria-label={t('settings.platform.billing.history.amount', { defaultValue: 'Amount ({{currency}})', currency: billing.currency.toUpperCase() })}
                          className="themed-input border rounded px-2 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="themed-text-secondary text-[11px]">
                          {t('settings.platform.billing.history.reference', { defaultValue: 'Reference' })}
                        </span>
                        <input
                          value={payReference}
                          onChange={e => setPayReference(e.target.value)}
                          disabled={paySaving}
                          aria-label={t('settings.platform.billing.history.reference', { defaultValue: 'Reference' })}
                          className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500 disabled:opacity-50"
                        />
                      </label>
                      <button
                        onClick={() => submitPayment(term)}
                        disabled={paySaving}
                        className="px-2.5 py-1 text-[11px] rounded themed-btn-primary disabled:opacity-50"
                      >
                        {t('settings.platform.billing.history.recordSubmit', { defaultValue: 'Record payment' })}
                      </button>
                      {payError && <p className="text-red-400 text-[10px] w-full">{payError}</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* ── Add a subscription ─────────────────────────────────────────────── */}
      {canWrite && (
        <div className="mt-3">
          <ActionDisclosure
            // Open on mount when a layout swap carried an OPEN add form over (#897) —
            // its open state is kept, not inferred from typed values (Cancel closes
            // it but keeps them, and must not reopen it).
            defaultOpen={addOpen}
            onOpenChange={setAddOpen}
            label={t('settings.platform.billing.history.addSubscription', { defaultValue: 'Add subscription' })}
            openLabel={t('common:cancel', { defaultValue: 'Cancel' })}
            hint={t('settings.platform.billing.history.addHint', {
              defaultValue: 'A plan, a start date and a payment cycle — the tenant\'s next subscription term.',
            })}
          >
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1">
                <span className="themed-text-secondary text-[11px]">
                  {t('settings.platform.billing.history.addPlan', { defaultValue: 'Plan' })}
                </span>
                <select
                  value={addPlanId}
                  onChange={e => setAddPlanId(e.target.value)}
                  disabled={addSaving}
                  aria-label={t('settings.platform.billing.history.addPlan', { defaultValue: 'Plan' })}
                  className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500 disabled:opacity-50"
                >
                  <option value="">
                    {t('settings.platform.billing.history.addPlanPlaceholder', { defaultValue: 'Choose a plan…' })}
                  </option>
                  {activePlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="themed-text-secondary text-[11px]">
                  {t('settings.platform.billing.history.addEffectiveDate', { defaultValue: 'Start date' })}
                </span>
                <input
                  type="date"
                  value={addDate}
                  onChange={e => setAddDate(e.target.value)}
                  disabled={addSaving}
                  aria-label={t('settings.platform.billing.history.addEffectiveDate', { defaultValue: 'Start date' })}
                  className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500 disabled:opacity-50"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="themed-text-secondary text-[11px]">
                  {t('settings.platform.billing.history.addCycleCount', { defaultValue: 'Every' })}
                </span>
                <input
                  value={addCycleCount}
                  onChange={e => setAddCycleCount(e.target.value)}
                  disabled={addSaving}
                  inputMode="numeric"
                  aria-label={t('settings.platform.billing.history.addCycleCount', { defaultValue: 'Every' })}
                  className="themed-input border rounded px-2 py-1 text-xs w-14 outline-none focus:border-blue-500 disabled:opacity-50"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="themed-text-secondary text-[11px]">
                  {t('settings.platform.billing.history.addCycleUnit', { defaultValue: 'Unit' })}
                </span>
                <select
                  value={addCycleUnit}
                  onChange={e => setAddCycleUnit(e.target.value as CycleUnit)}
                  disabled={addSaving}
                  aria-label={t('settings.platform.billing.history.addCycleUnit', { defaultValue: 'Unit' })}
                  className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500 disabled:opacity-50"
                >
                  {CYCLE_UNITS.map(u => (
                    <option key={u} value={u}>
                      {t(`settings.platform.billing.history.unit.${u}`, {
                        defaultValue: { day: 'day', week: 'week', month: 'month', year: 'year' }[u],
                      })}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="themed-text-secondary text-[11px]">
                  {t('settings.platform.billing.history.addAmount', { defaultValue: 'Payment amount ({{currency}})', currency: billing.currency.toUpperCase() })}
                </span>
                <input
                  value={addAmountText}
                  onChange={e => setAddAmountText(e.target.value)}
                  disabled={addSaving}
                  inputMode="decimal"
                  placeholder={t('settings.platform.billing.history.addAmountPlaceholder', { defaultValue: 'Optional' })}
                  aria-label={t('settings.platform.billing.history.addAmount', { defaultValue: 'Payment amount ({{currency}})', currency: billing.currency.toUpperCase() })}
                  className="themed-input border rounded px-2 py-1 text-xs w-28 outline-none focus:border-blue-500 disabled:opacity-50"
                />
              </label>
              <button
                onClick={submitAdd}
                disabled={addSaving}
                className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
              >
                {addSaving ? t('common:saving') : t('settings.platform.billing.history.addSubmit', { defaultValue: 'Add' })}
              </button>
            </div>
            {addError && <p className="text-red-400 text-[10px] mt-1">{addError}</p>}
          </ActionDisclosure>
        </div>
      )}
    </Section>
  )
}

function StatusChip({
  status, t,
}: {
  status: TermBillingRow['status']
  t: (k: string, o?: Record<string, unknown>) => string
}) {
  const cls = status === 'paid'
    ? 'bg-emerald-500/20 text-emerald-300'
    : status === 'overdue'
      ? 'bg-red-500/20 text-red-300'
      : status === 'underpaid'
        ? 'bg-amber-500/20 text-amber-300'
        : 'themed-bg-tertiary themed-text-muted'
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`}>
      {t(`settings.platform.billing.history.status.${status}`, {
        defaultValue: { paid: 'Paid', overdue: 'Overdue', untracked: 'Untracked', underpaid: 'Underpaid' }[status],
      })}
    </span>
  )
}
